import React from 'react'
import PropTypes from 'prop-types'

import { map, reduce, compact, at, size, has, isEmpty, isFunction } from 'lodash'

import classes from 'classnames'

import LoadingIndicator from '../components/LoadingIndicator'

import './Table.css'

const Table = (props) => {
    const {
        columns, data, footer, onRowClick, noDataText,
        withHeader, withBorders, withHover, withNumbers,
        isStriped, isCondensed, isLoading, isResponsive
    } = props

    const parsers = reduce(columns, (acc, { key, parse }) => {
        if (!isFunction(parse)) {
            return acc
        }
        return {
            ...acc,
            [key]: parse
        }
    }, {})

    const parsedData = map(data, (rowData) => (
        reduce(rowData, (acc, cellData, key) => {
            if (has(parsers, key)) {
                return {
                    ...acc,
                    [key]: parsers[key]({ rowData, cellData })
                }
            }

            return {
                ...acc,
                [key]: cellData
            }
        }, {})
    ))

    const totals = compact(map(columns, 'total'))

    const hasTotals = !isEmpty(totals) && !isEmpty(parsedData)

    const columnsCount = withNumbers ? size(columns) + 1 : size(columns)

    return (
        <div
            className={
                classes(
                    'noSwipe',
                    { 'table-responsive': isResponsive },
                    { 'be-loading': isLoading },
                    { 'be-loading-active': isLoading }
                )
            }
        >
            <table
                className={
                    classes(
                        'table',
                        { 'table-striped': isStriped },
                        { 'table-condensed': isCondensed },
                        { 'table-hover': withHover },
                        { 'table-borderless': withBorders === false }
                    )
                }
            >
                { withHeader && (
                    <thead>
                        <tr>
                            <th>Nr.</th>
                            { map(columns, ({ key, title, className }) => (
                                <th key={ key } className={ className }>
                                    { title }
                                </th>
                            )) }
                        </tr>
                    </thead>
                ) }
                <tbody className="no-border-x no-border-y">
                    { isEmpty(parsedData)
                        ? <tr><td colSpan={ columnsCount }>{ noDataText }</td></tr>
                        : map(parsedData, (rowData, index) => (
                            <tr
                                key={ `row-${index}` }
                                onClick={ () => isFunction(onRowClick) && onRowClick({ rowData, index }) }
                                style={
                                    isFunction(onRowClick) ? {
                                        cursor: 'pointer'
                                    } : {}
                                }
                            >
                                { withNumbers && (
                                    <td>
                                        { index + 1 }
                                        .
                                    </td>
                                ) }
                                { map(columns, ({ key, render, className }) => {
                                    const cellData = at(rowData, key)[0]
                                    return (
                                        <td
                                            key={ `cell-${index}-${key}` }
                                            className={ className }
                                        >
                                            {
                                                isFunction(render)
                                                    ? render({ index, cellData, rowData })
                                                    : cellData
                                            }
                                        </td>
                                    )
                                }) }
                            </tr>
                        )) }
                </tbody>
                { (hasTotals || footer) && (
                    <tfoot>
                        { hasTotals && (
                            <tr className="totals">
                                <td>{ size(parsedData) }</td>
                                { map(columns, ({ key, total }) => (
                                    <td key={ `total-${key}` }>
                                        { isFunction(total) ? total(parsedData) : null }
                                    </td>
                                )) }
                            </tr>
                        ) }
                        { footer && (
                            <tr className="footer">
                                <td colSpan={ columnsCount }>{ footer }</td>
                            </tr>
                        ) }
                    </tfoot>
                ) }
            </table>
            <LoadingIndicator isVisible={ isLoading } />
        </div>
    )
}

Table.propTypes = {
    columns      : PropTypes.arrayOf(PropTypes.object),
    data         : PropTypes.arrayOf(PropTypes.object),
    footer       : PropTypes.node,
    noDataText   : PropTypes.string,
    onRowClick   : PropTypes.func,
    withHeader   : PropTypes.bool,
    withBorders  : PropTypes.bool,
    withHover    : PropTypes.bool,
    withNumbers  : PropTypes.bool,
    isStriped    : PropTypes.bool,
    isCondensed  : PropTypes.bool,
    isLoading    : PropTypes.bool,
    isResponsive : PropTypes.bool
}

Table.defaultProps = {
    noDataText   : 'No data available.',
    withHeader   : true,
    withBorders  : true,
    withHover    : true,
    withNumbers  : true,
    isStriped    : true,
    isCondensed  : false,
    isLoading    : false,
    isResponsive : true
}

export default Table
